import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Search, RefreshCw, User, Phone, Mail, Calendar, Droplet, Heart, Activity, Thermometer, MapPin, Building2, ClipboardList, Users, Stethoscope, Edit2, Save, X, Camera, Upload, Shield, Eye } from 'lucide-react';
import { patientAPI } from '../../../api/patient';
import appointmentAPI from '../../../api/appointment';
import emrAPI from '../../../api/emr';
import userAPI from '../../../api/user';
import prescriptionAPI from '../../../api/prescription';
import { generatePrescriptionPDF, generatePrintHTML } from '../../doctor-dashboard/utils/pdfGenerator';
import { CARD_STYLES, BUTTON_STYLES, INPUT_STYLES } from '../utils/constants';
import { formatISTDateLong, formatISTDateTimeShort, formatISTDateShort } from '../utils/date';
import { useToast } from '../../../hooks/useToast';

const Row = ({ label, children }) => (
  <div className="flex gap-5 text-sm py-1">
    <div className="w-36 text-gray-500">{label}</div>
    <div className="flex-1 text-gray-800">{children}</div>
  </div>
);

const toneClassMap = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200'
};

const Badge = ({ children, tone = 'blue' }) => {
  const toneClasses = toneClassMap[tone] || toneClassMap.blue;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${toneClasses}`}>{children}</span>
  );
};

const PatientDetails = () => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patients, setPatients] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  // EMR state (read-only display)
  const [emrSummary, setEmrSummary] = useState(null);
  const [emrLoading, setEmrLoading] = useState(false);
  const [emrError, setEmrError] = useState('');
  // Appointment detail modal state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  // Prescription print state
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [duplicateFlags, setDuplicateFlags] = useState({});
  // Patient report state
  const [generatingPatientReport, setGeneratingPatientReport] = useState(false);
  const [patientReportDuplicate, setPatientReportDuplicate] = useState(false);
  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [doctors, setDoctors] = useState([]);

  const fetchPatients = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit, search, ...opts };
      const res = await patientAPI.list(params);
      const items = res?.data?.data?.items || res?.data?.items || res?.data?.patients || res?.items || res?.patients || [];

      // Extract pagination info - API returns currentPage, totalPages
      const rawPagination = res?.data?.data?.pagination || res?.data?.pagination || res?.pagination || {};
      const pageInfo = {
        page: rawPagination.currentPage || rawPagination.page || params.page,
        pages: rawPagination.totalPages || rawPagination.pages || 1,
        total: rawPagination.totalItems || rawPagination.total || items.length,
        limit: params.limit,
        hasNext: rawPagination.hasNext,
        hasPrev: rawPagination.hasPrev
      };

      setPatients(items);
      setPagination(pageInfo);
      if (!selected && items.length) setSelected(items[0]);
      setError('');

      if (search) {
        showInfo(`Found ${items.length} patient${items.length !== 1 ? 's' : ''} matching "${search}"`);
      }
    } catch (e) {
      console.error('Failed to load patients:', e);
      const errorMsg = e?.message || 'Failed to load patients';
      setError(errorMsg);
      showError(errorMsg);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, selected]);

  const refresh = () => fetchPatients();

  useEffect(() => { fetchPatients({ page: 1 }); }, []); // initial load

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await userAPI.getDoctors();
      const doctorsList = response?.data?.doctors
        || response?.data?.users
        || response?.doctors
        || response?.users
        || (Array.isArray(response?.data) ? response.data : [])
        || [];
      setDoctors(doctorsList);
    } catch (error) {
      console.error('Failed to load doctors:', error);
    }
  }, []);

  const fetchEmrSummary = useCallback(async (patientId) => {
    if (!patientId) return;
    setEmrLoading(true);
    setEmrError('');
    try {
      const res = await emrAPI.getPatientSummary(patientId);
      // API may return data at different nesting; support common shapes
      const data = res?.data || res?.summary || res;
      setEmrSummary(data || null);
    } catch (e) {
      console.error('Failed to load EMR summary:', e);
      const msg = e?.response?.data?.message || e?.message || 'Failed to load EMR summary';
      setEmrError(msg);
      setEmrSummary(null);
    } finally {
      setEmrLoading(false);
    }
  }, []);

  const fetchAppointments = useCallback(async (patientId) => {
    if (!patientId) return;
    setAppointmentsLoading(true);
    try {
      // Use API helper that ensures includeDoctor in response
      const res = await appointmentAPI.getByPatient(patientId, { limit: 100 });
      const items = res?.data?.items || res?.data?.appointments || res?.items || res?.appointments || [];
      setAppointments(items);
    } catch (e) {
      console.error('Failed to load appointments:', e);
      setAppointments([]);
    } finally {
      setAppointmentsLoading(false);
    }
  }, []);

  // Safely extract a patient object from various API response shapes
  const extractPatient = (resObj, fallback) => {
    const candidate = resObj?.data?.data?.patient
      || resObj?.data?.patient
      || resObj?.patient
      || resObj?.data; // sometimes APIs return the entity directly in data

    // If candidate looks like an API wrapper (has success/message), ignore it
    const looksLikeWrapper = candidate && (typeof candidate.success === 'boolean' || typeof candidate.message === 'string');
    const patient = looksLikeWrapper ? candidate?.patient : candidate;
    // Ensure we return an object with minimal identifiers
    return patient && (patient._id || patient.id) ? patient : (fallback || null);
  };

  const handleRowClick = async (p) => {
    try {
      setSelected(p);
      setEditMode(false);
      // Fetch fresh detail by ID
      const res = await patientAPI.getById(p._id || p.id);
      const detail = extractPatient(res, p) || p;

      // Prefer normalized keys
      const normalized = {
        ...detail,
        id: detail._id || detail.id,
        // Prefer `dob`, fallback to `dateOfBirth`
        dob: detail.dob || detail.dateOfBirth || detail.DOB,
        // Normalize sex display casing if only lowercase is present
        sex: detail.sex || (detail.gender ? detail.gender.toLowerCase() : undefined),
        // Ensure personalAddress exists
        personalAddress: {
          street: '', city: '', state: '', zipCode: '', country: '',
          ...(detail.personalAddress || {})
        },
        // Some payloads include assignedDoctor as populated object; keep original but also expose id for updates
        assignedDoctor: detail.assignedDoctor
      };

      setSelected(normalized);
      setEditData(normalized);
      // Fetch appointments for this patient
      fetchAppointments(normalized._id || normalized.id);
      // Fetch EMR data (read-only)
      const pid = normalized._id || normalized.id;
      fetchEmrSummary(pid);
    } catch (e) {
      // fallback keeps basic row data
      console.warn('Failed to fetch patient by id, using list item', e);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    fetchDoctors();

    const doctorId = (selected.assignedDoctor && (selected.assignedDoctor._id || selected.assignedDoctor.id))
      || (typeof selected.assignedDoctor === 'string' ? selected.assignedDoctor : '');

    setEditData({
      ...selected,
      assignedDoctor: doctorId,
      personalAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        ...selected.personalAddress
      },
      officeDetail: {
        company: '',
        designation: '',
        officeAddress: '',
        ...selected.officeDetail
      },
      drugAllergies: Array.isArray(selected.drugAllergies) ? selected.drugAllergies : [],
      allergies: Array.isArray(selected.allergies) ? selected.allergies : [],
      currentMedications: Array.isArray(selected.currentMedications) ? selected.currentMedications : []
    });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditData({ ...selected });
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);

      // Prefer the explicitly selected doctor (string id) from editData first,
      // then fall back to object forms, and finally to the existing selected value.
      const assignedDoctorId =
        (typeof editData.assignedDoctor === 'string' && editData.assignedDoctor)
        || (editData.assignedDoctor && (editData.assignedDoctor._id || editData.assignedDoctor.id))
        || (typeof selected.assignedDoctor === 'string' && selected.assignedDoctor)
        || (selected.assignedDoctor && (selected.assignedDoctor._id || selected.assignedDoctor.id))
        || undefined;

      const updatePayload = {
        firstName: editData.firstName || '',
        lastName: editData.lastName || '',
        mobile: editData.mobile || '',
        // Only include email if it has a value (email is optional)
        ...(editData.email?.trim() && { email: editData.email.trim() }),
        // API expects lowercase enum: male/female/other
        sex: (editData.sex || selected.sex || 'male')?.toString().toLowerCase(),
        // DOB
        dob: editData.dob || selected.dob || selected.dateOfBirth,
        // Blood Group
        bloodGroup: editData.bloodGroup || selected.bloodGroup || 'Unknown',
        // API expects an ObjectId string, not an object
        assignedDoctor: assignedDoctorId,
        personalAddress: {
          street: editData.personalAddress?.street || '',
          city: editData.personalAddress?.city || '',
          state: editData.personalAddress?.state || '',
          zipCode: editData.personalAddress?.zipCode || '',
          country: editData.personalAddress?.country || ''
        },
        // Backend expects `officeDetail` as a string (legacy). Safely serialize if UI captures structured values.
        officeDetail: editData.officeDetail?.officeAddress
          || editData.officeDetail?.company
          || editData.officeDetail?.designation
          || '',
        
        // Insurance Details
        policyNumber: editData.policyNumber || '',
        certificateNumber: editData.certificateNumber || '',
        companyTpaId: editData.companyTpaId || '',
        abhaNumber: editData.abhaNumber || '',
        insuranceCommencementDate: editData.insuranceCommencementDate || null,
        sumInsured: editData.sumInsured || null,

        // Medical history fields
        pastHistory: editData.pastHistory || '',
        medicalHistory: editData.medicalHistory || '',
        drugAllergies: Array.isArray(editData.drugAllergies) ? editData.drugAllergies : [],
        allergies: Array.isArray(editData.allergies) ? editData.allergies : [],
        currentMedications: Array.isArray(editData.currentMedications) ? editData.currentMedications : [],
        isActive: editData.isActive !== undefined ? editData.isActive : true
      };

      const res = await patientAPI.update(selected._id || selected.id, updatePayload);
      const updated = extractPatient(res) || res?.data || res;
      const normalizedUpdated = {
        ...updated,
        id: updated?._id || updated?.id,
        dob: updated?.dob || updated?.dateOfBirth,
        sex: updated?.sex || (updated?.gender ? updated.gender.toLowerCase() : undefined),
        assignedDoctor: updated?.assignedDoctor || null,
        personalAddress: {
          street: '', city: '', state: '', zipCode: '', country: '',
          ...(updated?.personalAddress || {})
        },
        drugAllergies: updated?.drugAllergies || [],
        allergies: updated?.allergies || [],
        currentMedications: updated?.currentMedications || []
      };
      setSelected(normalizedUpdated);
      setEditData(normalizedUpdated);
      setEditMode(false);
      setError('');
      showSuccess(`Patient ${normalizedUpdated.firstName} ${normalizedUpdated.lastName} updated successfully`);
      // Refresh patient list
      fetchPatients();
    } catch (e) {
      console.error('Failed to update patient:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to update patient';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value
        }
      }));
    } else {
      setEditData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Helper functions for array fields
  const addArrayItem = (field, value) => {
    if (!value || !value.trim()) return;
    setEditData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()]
    }));
  };

  const removeArrayItem = (field, index) => {
    setEditData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const vitals = useMemo(() => selected?.vitals || {}, [selected]);
  const recentVitals = useMemo(() => {
    // Prefer EMR summary recentVitals if available; fallback to selected.vitals
    const rv = emrSummary?.recentVitals || {};
    return Object.keys(rv || {}).length ? rv : vitals;
  }, [emrSummary, vitals]);
  // Group appointments by doctor for the "Doctors Visited" tab
  const visitsByDoctor = useMemo(() => {
    // Prefer EMR summary appointments as they include prescriptions with tests
    const appointmentsList = emrSummary?.allAppointments || appointments || [];
    if (!Array.isArray(appointmentsList) || appointmentsList.length === 0) return [];
    const map = new Map();
    const toDate = (apt) => {
      const dt = apt?.dateTime || (apt?.appointmentDate && apt?.appointmentTime ? `${apt.appointmentDate}T${apt.appointmentTime}` : apt?.appointmentDate);
      return dt ? new Date(dt) : null;
    };

    appointmentsList.forEach((apt) => {
      const doc = apt.doctor || {};
      const doctorId = doc._id || apt.doctor || apt.doctorId || 'unknown';
      const doctorName = doc.name || apt.doctorName || 'Doctor';
      const apptDate = toDate(apt);
      const entry = map.get(doctorId) || {
        doctorId,
        doctorName,
        appointments: [],
        firstVisitDate: null,
        lastVisitDate: null,
        visitCount: 0
      };
      entry.appointments.push(apt);
      entry.visitCount += 1;
      if (apptDate) {
        if (!entry.firstVisitDate || apptDate < entry.firstVisitDate) entry.firstVisitDate = apptDate;
        if (!entry.lastVisitDate || apptDate > entry.lastVisitDate) entry.lastVisitDate = apptDate;
      }
      map.set(doctorId, entry);
    });

    // Sort doctors by last visit desc
    return Array.from(map.values()).sort((a, b) => {
      const aTime = a.lastVisitDate ? a.lastVisitDate.getTime() : 0;
      const bTime = b.lastVisitDate ? b.lastVisitDate.getTime() : 0;
      return bTime - aTime;
    });
  }, [emrSummary, appointments]);
  const displaySex = useMemo(() => {
    if (!selected) return '';
    if (selected.gender) return selected.gender;
    if (selected.sex) return selected.sex.charAt(0).toUpperCase() + selected.sex.slice(1);
    return '';
  }, [selected]);

  // Sort appointments by date desc and take recent few for detailed view
  const recentAppointments = useMemo(() => {
    // Prefer EMR summary appointments as they include prescriptions with tests
    const appointmentsList = emrSummary?.allAppointments || appointments || [];
    if (!Array.isArray(appointmentsList)) return [];
    const toDate = (apt) => {
      const dt = apt?.dateTime || (apt?.appointmentDate && apt?.appointmentTime ? `${apt.appointmentDate}T${apt.appointmentTime}` : apt?.appointmentDate);
      return dt ? new Date(dt) : null;
    };
    return [...appointmentsList]
      .sort((a, b) => {
        const at = toDate(a)?.getTime() || 0;
        const bt = toDate(b)?.getTime() || 0;
        return bt - at;
      })
      .slice(0, 3);
  }, [emrSummary, appointments]);

  const openAppointmentDetail = (apt) => {
    setSelectedAppointment(apt);
    setShowAppointmentDetail(true);
  };

  const closeAppointmentDetail = () => {
    setShowAppointmentDetail(false);
    setSelectedAppointment(null);
  };

  const toggleDuplicate = (prescriptionId, event) => {
    event.stopPropagation();
    setDuplicateFlags(prev => ({
      ...prev,
      [prescriptionId]: !prev[prescriptionId]
    }));
  };

  const formatTimingSlots = (timing = {}) => {
    const slots = [];
    if (timing.morning) slots.push('Morning');
    if (timing.noon) slots.push('Noon');
    if (timing.evening) slots.push('Evening');
    if (timing.night) slots.push('Night');
    return slots.join(', ');
  };

  const handleDownloadPDF = async (prescription, event) => {
    event.stopPropagation();
    setGeneratingPDF(prescription._id);

    try {
      console.log('[Patient Details] Fetching full prescription data for:', prescription._id);
      const response = await prescriptionAPI.getPrescriptionById(prescription._id);

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch prescription data');
      }

      const fullPrescription = response.data;
      console.log('[Patient Details] Full prescription data:', fullPrescription);

      const patientData = {
        firstName: fullPrescription.patient?.firstName || selected?.firstName || selected?.name || 'N/A',
        lastName: fullPrescription.patient?.lastName || selected?.lastName || '',
        sex: fullPrescription.patient?.sex || selected?.sex || selected?.gender || 'N/A',
        gender: fullPrescription.patient?.sex || selected?.sex || selected?.gender,
        mobile: fullPrescription.patient?.mobile || selected?.mobile || selected?.phone || 'N/A',
        dob: fullPrescription.patient?.dob || selected?.dob,
        dateOfBirth: fullPrescription.patient?.dateOfBirth || selected?.dateOfBirth,
        age: fullPrescription.patient?.age || selected?.age
      };

      const doctorData = fullPrescription.doctor ? {
        firstName: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.firstName
          : undefined,
        lastName: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.lastName
          : undefined,
        name: typeof fullPrescription.doctor === 'object'
          ? (fullPrescription.doctor.name ||
            [fullPrescription.doctor.firstName, fullPrescription.doctor.lastName].filter(Boolean).join(' ').trim() ||
            'N/A')
          : fullPrescription.doctor,
        specialization: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.specialization
          : undefined,
        qualification: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.qualification
          : undefined,
        licenseNumber: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.licenseNumber
          : undefined,
        registrationNumber: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.registrationNumber
          : undefined
      } : null;

      const prescriptionData = {
        language: fullPrescription.language || 'E',
        items: fullPrescription.items || [],
        tests: fullPrescription.tests || [],
        imagingTests: fullPrescription.imagingTests || [],
        foodAdvice: fullPrescription.foodAdvice || '',
        knownCaseDisease: fullPrescription.knownCaseDisease,
        appointmentDate: fullPrescription.appointmentDate || fullPrescription.dateTime || selectedAppointment?.dateTime,
        vitals: fullPrescription.vitals || {},
        chiefComplaint: fullPrescription.chiefComplaint || fullPrescription.complaint || '',
        physicalExaminationFindings: fullPrescription.physicalExaminationFindings || fullPrescription.examination || ''
      };

      const selectedSlogan = fullPrescription.sloganRef ? {
        slogan: typeof fullPrescription.sloganRef === 'object'
          ? fullPrescription.sloganRef.slogan
          : fullPrescription.sloganRef
      } : null;

      console.log('[Patient Details] Generating PDF with data:', { prescriptionData, patientData, doctorData });

      const isDuplicate = duplicateFlags[prescription._id] || false;

      const result = await generatePrescriptionPDF(
        prescriptionData,
        patientData,
        selectedSlogan,
        isDuplicate,
        { testsRecords: fullPrescription.tests || [], imagingTestsRecords: fullPrescription.imagingTests || [], doctorData }
      );

      if (!result.success) {
        showError('Error generating PDF: ' + result.error);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to generate PDF: ' + (error.message || 'Please try again.'));
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handlePrint = async (prescription, event) => {
    event.stopPropagation();

    try {
      console.log('[Patient Details] Fetching full prescription data for print:', prescription._id);
      const response = await prescriptionAPI.getPrescriptionById(prescription._id);

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch prescription data');
      }

      const fullPrescription = response.data;
      console.log('[Patient Details] Full prescription data for print:', fullPrescription);

      const patientData = {
        firstName: fullPrescription.patient?.firstName || selected?.firstName || selected?.name || 'N/A',
        lastName: fullPrescription.patient?.lastName || selected?.lastName || '',
        sex: fullPrescription.patient?.sex || selected?.sex || selected?.gender || 'N/A',
        gender: fullPrescription.patient?.sex || selected?.sex || selected?.gender,
        mobile: fullPrescription.patient?.mobile || selected?.mobile || selected?.phone || 'N/A',
        email: fullPrescription.patient?.email || selected?.email,
        dob: fullPrescription.patient?.dob || selected?.dob,
        dateOfBirth: fullPrescription.patient?.dateOfBirth || selected?.dateOfBirth,
        age: fullPrescription.patient?.age || selected?.age
      };

      const doctorData = fullPrescription.doctor ? {
        firstName: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.firstName
          : undefined,
        lastName: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.lastName
          : undefined,
        name: typeof fullPrescription.doctor === 'object'
          ? (fullPrescription.doctor.name ||
            [fullPrescription.doctor.firstName, fullPrescription.doctor.lastName].filter(Boolean).join(' ').trim() ||
            'N/A')
          : fullPrescription.doctor,
        specialization: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.specialization
          : undefined,
        qualification: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.qualification
          : undefined,
        licenseNumber: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.licenseNumber
          : undefined,
        registrationNumber: typeof fullPrescription.doctor === 'object'
          ? fullPrescription.doctor.registrationNumber
          : undefined
      } : null;

      // Merge vitals, complaint, and examination from prescription and appointment
      const mergedVitals = {
        ...(fullPrescription.vitals || {}),
        ...(selectedAppointment?.vitals || {}),
      };

      const mergedComplaint = (
        fullPrescription.chiefComplaint ||
        selectedAppointment?.chiefComplaint ||
        selectedAppointment?.complaint ||
        selectedAppointment?.presentingComplaint ||
        ''
      );

      const mergedExamination = (
        fullPrescription.physicalExaminationFindings ||
        selectedAppointment?.physicalExaminationFindings ||
        selectedAppointment?.examination ||
        selectedAppointment?.examFindings ||
        ''
      );

      const prescriptionData = {
        language: fullPrescription.language || 'E',
        // Clinical info for print parity
        vitals: mergedVitals,
        chiefComplaint: mergedComplaint,
        physicalExaminationFindings: mergedExamination,
        // Items and tests
        items: fullPrescription.items || [],
        tests: fullPrescription.tests || [],
        imagingTests: fullPrescription.imagingTests || [],
        foodAdvice: fullPrescription.foodAdvice || '',
        knownCaseDisease: fullPrescription.knownCaseDisease,
        appointmentDate: fullPrescription.appointmentDate || fullPrescription.dateTime || selectedAppointment?.dateTime
      };

      const selectedSlogan = fullPrescription.sloganRef ? {
        slogan: typeof fullPrescription.sloganRef === 'object'
          ? fullPrescription.sloganRef.slogan
          : fullPrescription.sloganRef
      } : null;


      const isDuplicate = duplicateFlags[prescription._id] || false;

      const printHTML = generatePrintHTML(
        prescriptionData,
        patientData,
        selectedSlogan,
        isDuplicate,
        {
          testsRecords: fullPrescription.tests || [],
          imagingTestsRecords: fullPrescription.imagingTests || [],
          doctorData
        }
      );

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printHTML);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error('Error printing:', error);
      showError('Failed to print prescription: ' + (error.message || 'Please try again.'));
    }
  };

  const handleDownloadPatientReport = async () => {
    if (!selectedAppointment) return;

    setGeneratingPatientReport(true);
    try {
      console.log('[Patient Details] Fetching patient report for appointment:', selectedAppointment._id);
      const response = await prescriptionAPI.getPatientReportWithPrescription(selectedAppointment._id);

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch patient report data');
      }

      const reportData = response.data;
      console.log('[Patient Details] Patient report data:', reportData);

      // Prepare patient data with complete medical history
      const patientData = {
        firstName: reportData.patient.firstName || 'N/A',
        lastName: reportData.patient.lastName || '',
        mobile: reportData.patient.mobile || 'N/A',
        email: reportData.patient.email || '',
        sex: reportData.patient.sex || 'N/A',
        gender: reportData.patient.sex,
        dob: reportData.patient.dob,
        age: reportData.patient.age,
        bloodGroup: reportData.patient.bloodGroup || 'N/A',
        externalId: reportData.patient.externalId,
        // Medical history fields
        drugAllergies: reportData.patient.drugAllergies || [],
        allergies: reportData.patient.allergies || [],
        currentMedications: reportData.patient.currentMedications || [],
        pastHistory: reportData.patient.pastHistory || '',
        medicalHistory: reportData.patient.medicalHistory || ''
      };

      // Prepare doctor data
      const doctorData = {
        firstName: reportData.doctor.firstName,
        lastName: reportData.doctor.lastName,
        name: reportData.doctor.name,
        email: reportData.doctor.email,
        mobile: reportData.doctor.mobile,
        specialization: reportData.doctor.specialization,
        qualification: reportData.doctor.qualification,
        registrationNumber: reportData.doctor.registrationNumber
      };

      // Transform vitals to match PDF generator format
      const transformedVitals = reportData.vitals ? {
        bloodPressure: reportData.vitals.bpSys && reportData.vitals.bpDia
          ? `${reportData.vitals.bpSys}/${reportData.vitals.bpDia}`
          : null,
        pulse: reportData.vitals.pulse || null,
        temperature: reportData.vitals.temperature || null,
        oxygenSaturation: reportData.vitals.oxygenSaturation || null,
        respiration: reportData.vitals.respiration || null,
        sugar: reportData.vitals.sugar || null,
        height: reportData.vitals.heightCm || null,
        weight: reportData.vitals.weightKg || null,
        bmi: reportData.vitals.bmi || null
      } : null;

      // Prepare comprehensive prescription data including clinical info (without prescription items)
      const prescriptionData = {
        language: 'E',
        // Clinical examination data
        chiefComplaint: reportData.chiefComplaint || '',
        historyOfPresentIllness: reportData.historyOfPresentIllness || '',
        physicalExaminationFindings: reportData.physicalExaminationFindings || '',
        // Vitals
        vitals: transformedVitals,
        // Empty items array (no prescriptions in patient report)
        items: [],
        tests: [],
        foodAdvice: '',
        knownCaseDisease: null,
        // Flag to indicate this is a comprehensive report
        isComprehensiveReport: true,
        appointmentDate: reportData.appointmentDate
      };

      // Generate PDF using existing generator
      const result = await generatePrescriptionPDF(
        prescriptionData,
        patientData,
        null, // No slogan for comprehensive report
        patientReportDuplicate,
        {
          doctorData,
          isComprehensiveReport: true,
          reportType: 'patient_comprehensive'
        }
      );

      if (!result.success) {
        showError('Error generating patient report: ' + result.error);
      } else {
        showSuccess('Patient report downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading patient report:', error);
      showError('Failed to generate patient report: ' + (error.message || 'Please try again.'));
    } finally {
      setGeneratingPatientReport(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Invalid file type. Please upload JPG, PNG, GIF, BMP, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showError('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      setPhotoUploading(true);
      const patientId = selected._id || selected.id;
      const result = await patientAPI.uploadPhoto(patientId, file);

      // Update selected patient with new photo
      const photoData = result?.data?.photo || result?.photo;
      setSelected(prev => ({ ...prev, photo: photoData }));
      setEditData(prev => ({ ...prev, photo: photoData }));

      showSuccess('Patient photo uploaded successfully');

      // Refresh patient list to show updated photo
      fetchPatients();
    } catch (e) {
      console.error('Failed to upload photo:', e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to upload photo';
      showError(errorMessage);
    } finally {
      setPhotoUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerPhotoUpload = () => {
    fileInputRef.current?.click();
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });
      setWebcamStream(stream);
      setShowWebcam(true);

      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing webcam:', error);
      showError('Unable to access webcam. Please check permissions.');
    }
  };

  const stopWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(track => track.stop());
      setWebcamStream(null);
    }
    setShowWebcam(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(async (blob) => {
      if (!blob) {
        showError('Failed to capture photo');
        return;
      }

      // Create a file from blob
      const file = new File([blob], `patient_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

      try {
        setPhotoUploading(true);
        const patientId = selected._id || selected.id;
        const result = await patientAPI.uploadPhoto(patientId, file);

        // Update selected patient with new photo
        const photoData = result?.data?.photo || result?.photo;
        setSelected(prev => ({ ...prev, photo: photoData }));
        setEditData(prev => ({ ...prev, photo: photoData }));

        showSuccess('Patient photo captured and uploaded successfully');

        // Stop webcam and close modal
        stopWebcam();

        // Refresh patient list to show updated photo
        fetchPatients();
      } catch (e) {
        console.error('Failed to upload captured photo:', e);
        const errorMessage = e?.response?.data?.message || e?.message || 'Failed to upload photo';
        showError(errorMessage);
      } finally {
        setPhotoUploading(false);
      }
    }, 'image/jpeg', 0.9);
  };

  // Cleanup webcam on unmount
  useEffect(() => {
    return () => {
      if (webcamStream) {
        webcamStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [webcamStream]);


  return (
    <div className="flex-1 space-y-6">


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{fontFamily: 'sans-serif'}}>
        {/* List */}
        <div className="bg-white rounded-lg border-2 border-gray-300 shadow-sm overflow-hidden">

          <div className="bg-[#2D869C] px-5 py-4 flex items-center justify-between">
            <div className="font-semibold text-white text-xl flex items-center">
              <Users size={20} className="mr-2" />
              Patients
            </div>
            <div className="text-md text-white/90 font-semibold bg-white/20 px-3 py-1 rounded-full">{pagination.total} total</div>
          </div>
          <div className="bg-white rounded-lg border-2 border-gray-300 shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchPatients({ page: 1 }); }}
                  placeholder="Search by name, mobile, or patient ID"
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-md font-semibold"
                />
              </div>
              <button
                className="px-10 py-3 bg-[#2D869C] text-white rounded-lg hover:bg-[#9DC9D4] transition-colors font-semibold text-md "
                onClick={() => fetchPatients({ page: 1 })}
              >
                Search
              </button>
              <button
                className="px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-md flex items-center"
                onClick={refresh}
              >
                <RefreshCw size={16} className="mr-2" /> Refresh
              </button>
            </div>
            {error && (
              <div className="mt-3 text-sm text-red-600 font-medium">{error}</div>
            )}
          </div>
          <div className="divide-y divide-gray-200 max-h-[100%] overflow-auto">
            {loading ? (
              <div className="p-6 text-sm text-gray-600">Loading...</div>
            ) : patients.length ? (
              patients.map((p) => {
                const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim();
                const isActive = (selected?._id || selected?.id) === (p._id || p.id);
                return (
                  <button
                    key={p._id || p.id}
                    onClick={() => handleRowClick(p)}
                    className={`w-full text-left px-5 py-2 hover:bg-[#9DC9D4]/10 transition-colors ${isActive ? 'bg-[#9DC9D4]/20 border-l-4 border-[#2D869C]' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      {p.photo?.url ? (
                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={p.photo.url}
                            alt={fullName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-[#9DC9D4]/30 flex items-center justify-center flex-shrink-0">
                          <User size={28} className="text-[#2D869C]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-gray-900 text-lg truncate">{fullName || 'Unnamed Patient'}</div>
                          {p.externalId && <span className="px-2 py-0.5 bg-[#9DC9D4]/30 text-[#2D869C] rounded text-md font-semibold">{p.externalId}</span>}

                        </div>
                        <div className="text-md text-gray-600 mt-1 truncate font-semibold">{p.mobile || '—'} {p.email ? `· ${p.email}` : ''}</div>
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-6 text-sm text-gray-600">No patients found</div>
            )}
          </div>
          {/* Simple pagination controls */}
          <div className="border-t-2 border-gray-200 bg-gray-50 px-5 py-4 flex items-center justify-between text-sm">
            <div className="text-gray-700 font-semibold">Page {pagination.page} of {Math.max(pagination.pages || 1, 1)}</div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchPatients({ page: Math.max(1, (pagination.page || 1) - 1) })}
                disabled={pagination.hasPrev === false || (pagination.page || 1) <= 1}
                className="px-4 py-2 border-2 text-lg border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >Prev</button>
              <button
                onClick={() => fetchPatients({ page: (pagination.page || 1) + 1 })}
                disabled={pagination.hasNext === false || (pagination.pages && (pagination.page || 1) >= pagination.pages)}
                className="px-4 py-2 border-2 text-lg border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2" style={{ fontFamily: 'sans-serif' }}>
          {selected ? (
            <div className="bg-white rounded-lg border-2 border-gray-300 shadow-sm overflow-hidden">
              <div className="bg-[#2D869C] pr-6  flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Patient Photo */}
                  <div className="relative">
                    {selected.photo?.url ? (
                      <div className="w-28 h-28 rounded-xl overflow-hidden border-2 border-white shadow-md">
                        <img
                          src={selected.photo.url}
                          alt={`${selected.firstName || ''} ${selected.lastName || ''}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-white flex items-center justify-center"><svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-28 h-28 bg-white border-2 border-white shadow-lg flex items-center justify-center">
                        <User size={28} className="text-[#2D869C]" />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white  ">{`${selected.firstName || ''} ${selected.lastName || ''}`.trim() || 'Unnamed Patient'}</div>
                    <div className="text-md text-white/90 flex items-center gap-6 mt-3">
                      {selected.externalId && <span className="bg-white/20 px-2 py-0.5 rounded text-white font-semibold text-md">{selected.externalId}</span>}
                      {selected.bloodGroup && (
                        <span className="inline-flex items-center text-md font-semibold"><Droplet size={20} className="mr-1 text-white" /> {selected.bloodGroup}</span>
                      )}
                      {displaySex && (
                        <span className="inline-flex items-center text-md font-semibold"><Users size={20} className="mr-1 text-white" /> {displaySex}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-md text-white/80 font-semibold">
                    Created: {selected.createdAt ? new Date(selected.createdAt).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : '—'}
                  </div>
                  {!editMode ? (
                    <div className="flex gap-2">
                      <button
                        onClick={triggerPhotoUpload}
                        disabled={photoUploading}
                        className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-md font-semibold flex items-center"
                        title="Upload patient photo from file"
                      >
                        {photoUploading ? (
                          <RefreshCw size={16} className="mr-1 animate-spin" />
                        ) : (
                          <Upload size={16} className="mr-1" />
                        )}
                        {photoUploading ? 'Uploading...' : 'Upload'}
                      </button>
                      <button
                        onClick={startWebcam}
                        disabled={photoUploading}
                        className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-md font-semibold flex items-center"
                        title="Capture photo using webcam"
                      >
                        <Camera size={16} className="mr-1" />
                        Webcam
                      </button>
                      <button
                        onClick={handleEdit}
                        className="px-3 py-1.5 bg-white text-[#2D869C] rounded-lg hover:bg-white/90 transition-colors text-md font-semibold flex items-center"
                      >
                        <Edit2 size={16} className="mr-1" /> Edit
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={loading}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-lg font-semibold flex items-center disabled:opacity-50"
                      >
                        <Save size={20} className="mr-1" /> Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-lg font-bold flex items-center"
                      >
                        <X size={20} className="mr-1 font-bold" /> Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Hidden file input for photo upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contact */}
                <div>
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <Phone size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">Contact Information</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-4  space-y-2">
                    {editMode ? (
                      <>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="text-gray-600 font-medium text-lg w-40">Mobile</div>
                          <input
                            value={editData.mobile || ''}
                            onChange={(e) => handleEditChange('mobile', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="text-gray-600 font-medium text-lg w-40">Email</div>
                          <input
                            value={editData.email || ''}
                            onChange={(e) => handleEditChange('email', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"

                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="text-gray-600 font-medium text-lg w-40">Assigned Doctor</div>
                          <select
                            value={(typeof editData.assignedDoctor === 'string'
                              ? editData.assignedDoctor
                              : (editData.assignedDoctor?._id || editData.assignedDoctor?.id || ''))}
                            onChange={(e) => handleEditChange('assignedDoctor', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          >
                            <option value="">Select Doctor</option>
                            {doctors.map(doc => (
                              <option key={doc._id || doc.id} value={doc._id || doc.id}>
                                {doc.name || `${doc.firstName} ${doc.lastName}`.trim()} {doc.specialty || doc.specialization ? `- ${doc.specialty || doc.specialization}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="text-gray-600 font-medium text-lg w-40">DOB</div>
                          <input
                            type="date"
                            value={editData.dob ? new Date(editData.dob).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleEditChange('dob', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="text-gray-600 font-medium text-lg w-40">Age</div>
                          <div className="flex-1 text-gray-800 text-lg font-semibold">{selected.age !== undefined && selected.age !== null ? `${selected.age} years` : '—'}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex gap-9 text-sm">
                          <div className="w-36 text-gray-600 font-medium text-lg">Mobile</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.mobile || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-9">
                          <div className="w-36 text-gray-600 font-medium text-lg">Email</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.email || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-9">
                          <div className="w-36 text-gray-600 font-medium text-lg">Assigned Doctor</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.assignedDoctor?.name || selected.assignedDoctorName || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-9">
                          <div className="w-36 text-gray-600 font-medium text-lg">DOB</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{(selected.dob || selected.dateOfBirth) ? formatISTDateShort(selected.dob || selected.dateOfBirth) : '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-9">
                          <div className="w-36 text-gray-600 font-medium text-lg">Age</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.age !== undefined && selected.age !== null ? `${selected.age} years` : '—'}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <MapPin size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">Address</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-4 space-y-2">
                    {editMode ? (
                      <>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="w-36 text-gray-600 text-lg font-medium">Street</div>
                          <input
                            value={editData.personalAddress?.street || ''}
                            onChange={(e) => handleEditChange('personalAddress.street', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="w-36 text-gray-600 text-lg font-medium">City</div>
                          <input
                            value={editData.personalAddress?.city || ''}
                            onChange={(e) => handleEditChange('personalAddress.city', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="w-36 text-gray-600 text-lg font-medium">State</div>
                          <input
                            value={editData.personalAddress?.state || ''}
                            onChange={(e) => handleEditChange('personalAddress.state', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="w-36 text-gray-600 text-lg font-medium">Zip Code</div>
                          <input
                            value={editData.personalAddress?.zipCode || ''}
                            onChange={(e) => handleEditChange('personalAddress.zipCode', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                        <div className="flex items-center text-sm py-1.5">
                          <div className="w-36 text-gray-600 text-lg font-medium">Country</div>
                          <input
                            value={editData.personalAddress?.country || ''}
                            onChange={(e) => handleEditChange('personalAddress.country', e.target.value)}
                            className="flex-1  text-gray-900 font-medium text-lg px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex text-sm gap-7">
                          <div className="w-36 text-gray-600 font-medium text-lg">Street</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.personalAddress?.street || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-7">
                          <div className="w-36 text-gray-600 font-medium text-lg">City</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.personalAddress?.city || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-7">
                          <div className="w-36 text-gray-600 font-medium text-lg">State</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.personalAddress?.state || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-7 ">
                          <div className="w-36 text-gray-600 font-medium text-lg">Zip Code</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.personalAddress?.zipCode || '—'}</div>
                        </div>
                        <div className="flex text-sm py-1.5 gap-7">
                          <div className="w-36 text-gray-600 font-medium text-lg">Country</div>
                          <div className="flex-1 text-gray-900 font-medium text-lg">{selected.personalAddress?.country || '—'}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>


                {/* Insurance Details */}
                <div className="lg:col-span-2">
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <Shield size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">Insurance Details</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-5">
                    {editMode ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">Policy Number</div>
                            <input
                              value={editData.policyNumber || ''}
                              onChange={(e) => handleEditChange('policyNumber', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                              placeholder="Enter policy number"
                            />
                          </div>
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">Certificate No.</div>
                            <input
                              value={editData.certificateNumber || ''}
                              onChange={(e) => handleEditChange('certificateNumber', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                              placeholder="Enter certificate number"
                            />
                          </div>
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">Company / TPA ID</div>
                            <input
                              value={editData.companyTpaId || ''}
                              onChange={(e) => handleEditChange('companyTpaId', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                              placeholder="ID of TPA/Insurance Co."
                            />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">ABHA Number</div>
                            <input
                              value={editData.abhaNumber || ''}
                              onChange={(e) => handleEditChange('abhaNumber', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                              placeholder="Enter ABHA number"
                            />
                          </div>
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">Sum Insured</div>
                            <input
                              value={editData.sumInsured || ''}
                              onChange={(e) => handleEditChange('sumInsured', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                              placeholder="Amount in ₹"
                              type="number"
                            />
                          </div>
                          <div className="flex text-sm py-1.5 items-center">
                            <div className="w-40 text-gray-600 font-medium text-lg">Insurance Start Date</div>
                            <input
                              type="date"
                              value={editData.insuranceCommencementDate ? new Date(editData.insuranceCommencementDate).toISOString().split('T')[0] : ''}
                              onChange={(e) => handleEditChange('insuranceCommencementDate', e.target.value)}
                              className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div className="space-y-2">
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className="w-40 text-gray-600 font-medium text-lg">Policy Number</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.policyNumber || '—'}</div>
                          </div>
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className="w-40 text-gray-600 font-medium text-lg">Certificate No.</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.certificateNumber || '—'}</div>
                          </div>
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className="w-40 text-gray-600 font-medium text-lg">Company / TPA ID</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.companyTpaId || '—'}</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className="w-40 text-gray-600 font-medium text-lg">ABHA Number</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.abhaNumber || '—'}</div>
                          </div>
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className="w-40 text-gray-600 font-medium text-lg">Sum Insured</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.sumInsured ? `₹${selected.sumInsured}` : '—'}</div>
                          </div>
                          <div className="flex text-sm py-1.5 gap-5">
                            <div className=" text-gray-600 font-medium text-lg">Insurance Start Date</div>
                            <div className="flex-1 text-gray-900 font-medium text-lg">{selected.insuranceCommencementDate ? new Date(selected.insuranceCommencementDate).toLocaleDateString('en-IN') : '—'}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Medical Details */}
                <div className="lg:col-span-2">
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <ClipboardList size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">Medical Details</span>
                  </h4>
                  <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-5">
                    {editMode ? (
                      <div className="space-y-4">
                        {/* Drug Allergies */}
                        <div className="flex gap-7">
                          <div className="flex gap-10 items-center ">
                            <div className="text-lg w-32 font-medium text-gray-700 mb-20">Drug Allergies</div>
                            <div className="max-w-[20vw]">
                              <div className="flex gap-5 mt-3 ">
                                <input
                                  type="text"
                                  placeholder="Add drug allergy"
                                  className={` flex-1 px-3 py-2 border-2 w-[15.5vw] border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900`}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addArrayItem('drugAllergies', e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <button
                                  onClick={(e) => {
                                    const input = e.target.previousElementSibling;
                                    addArrayItem('drugAllergies', input.value);
                                    input.value = '';
                                  }}
                                  className={` text-lg px-3 py-1 border-2 border-[#2D869C] rounded-lg hover:bg-[#2D869C]/15 transition-colors font-medium`}
                                  type="button"
                                >
                                  Add
                                </button>
                              </div>
                              {(editData.drugAllergies || []).map((allergy, idx) => (
                                <span key={idx} className="inline-flex  items-center px-2.5 py-0.5 rounded-full text-md mt-3 font-semibold bg-red-100 text-red-800">
                                  {allergy}
                                  <button
                                    onClick={() => removeArrayItem('drugAllergies', idx)}
                                    className="ml-1 text-red-600 hover:text-red-800"
                                    type="button"
                                  >
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>

                          </div>

                          {/* General Allergies */}
                          <div className="flex gap- items-center ">
                            <div className="text-lg w-40 font-medium text-gray-700 mb-24">General Allergies</div>
                            <div className="max-w-[20vw]">
                              <div className="flex gap-5 mt-3 w-full">
                                <input
                                  type="text"
                                  placeholder="Add general allergy"
                                  className={` flex-1 px-3 py-2 border-2 w-[15.5vw] border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900`}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      addArrayItem('allergies', e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <button
                                  onClick={(e) => {
                                    const input = e.target.previousElementSibling;
                                    addArrayItem('allergies', input.value);
                                    input.value = '';
                                  }}
                                  className={` text-lg px-3 py-1 border-2 border-[#2D869C] rounded-lg hover:bg-[#2D869C]/15 transition-colors font-medium`}
                                  type="button"
                                >
                                  Add
                                </button>
                              </div>
                              {(editData.allergies || []).map((allergy, idx) => (
                                <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-md mt-3 font-semibold bg-yellow-100 text-yellow-800">
                                  {allergy}
                                  <button
                                    onClick={() => removeArrayItem('allergies', idx)}
                                    className="ml-1 text-yellow-600 hover:text-yellow-800"
                                    type="button"
                                  >
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Current Medications */}
                        <div className="flex gap-12 items-center">
                          <div className="text-lg font-medium w-20 text-gray-700 mb-2">Current Medications</div>
                          <div className="flex-1">
                            <div className="flex gap-5 mt-3 w-full">
                              <input
                                type="text"
                                placeholder="Add current medication"
                                className="flex-1 px-3 py-2 border-2 ml-8 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addArrayItem('currentMedications', e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                              />
                              <button
                                onClick={(e) => {
                                  const input = e.target.previousElementSibling;
                                  addArrayItem('currentMedications', input.value);
                                  input.value = '';
                                }}
                                className="text-lg px-3 py-1 border-2 border-[#2D869C] rounded-lg hover:bg-[#2D869C]/15 transition-colors font-medium"
                                type="button"
                              >
                                Add
                              </button>
                            </div>
                            {(editData.currentMedications || []).map((med, idx) => (
                              <span key={idx} className="inline-flex ml-8 items-center px-2.5 py-0.5 rounded-full text-md mt-3 font-semibold bg-blue-100 text-blue-800">
                                {med}
                                <button
                                  onClick={() => removeArrayItem('currentMedications', idx)}
                                  className="ml-1 text-blue-600 hover:text-blue-800"
                                  type="button"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Past Medical History */}
                        <div className="flex gap-2 ">
                          <div className="text-lg font-medium w-36 text-gray-700 mb-2">
                            Past Medical History</div>
                          <textarea
                            value={editData.pastHistory || ''}
                            onChange={(e) => handleEditChange('pastHistory', e.target.value)}
                            placeholder="Enter past medical history..."
                            className="flex-1 px-3 py-2 border-2 ml-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                            rows="4"
                          />
                        </div>

                        {/* Medical History */}
                        <div className="flex gap-2 ">
                          <div className="text-lg font-medium w-36 text-gray-700 mb-2">General Medical History</div>
                          <textarea
                            value={editData.medicalHistory || ''}
                            onChange={(e) => handleEditChange('medicalHistory', e.target.value)}
                            placeholder="Enter general medical history..."
                            className="flex-1 px-3 py-2 border-2 ml-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900"
                            rows="4"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Allergies Row - Side by Side */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                          <div className="flex gap-16">
                            <div className="text-lg font-medium text-gray-700 whitespace-nowrap">Drug Allergies</div>
                            {selected.drugAllergies && selected.drugAllergies.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {selected.drugAllergies.map((allergy, idx) => (
                                  <span key={idx} className="inline-flex font-semibold items-center px-2.5 py-0.5 rounded-full text-md bg-red-100 text-red-800">
                                    {allergy}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">—</div>
                            )}
                          </div>

                          <div className="flex gap-12">
                            <div className="text-lg font-medium text-gray-700 whitespace-nowrap">General Allergies</div>
                            {selected.allergies && selected.allergies.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {selected.allergies.map((allergy, idx) => (
                                  <span key={idx} className="inline-flex font-semibold items-center px-2.5 py-0.5 rounded-full text-md bg-yellow-100 text-yellow-800">
                                    {allergy}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">—</div>
                            )}
                          </div>
                        </div>

                        {/* Current Medications */}
                        <div className="flex gap-4">
                          <div className="text-lg font-medium text-gray-700 whitespace-nowrap">Current Medications</div>
                          {selected.currentMedications && selected.currentMedications.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {selected.currentMedications.map((med, idx) => (
                                <span key={idx} className="inline-flex font-semibold items-center px-2.5 py-0.5 rounded-full text-md bg-blue-100 text-blue-800">
                                  {med}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">—</div>
                          )}
                        </div>

                        {/* Past Medical History */}
                        <div className="flex gap-6">
                          <div className="text-lg font-medium text-gray-700 whitespace-nowrap ">Past Medical History</div>
                          <div className="text-lg text-gray-600 whitespace-pre-wrap flex-1">
                            {selected.pastHistory || '—'}
                          </div>
                        </div>

                        {/* General Medical History */}
                        <div className="flex gap-12">
                          <div className="text-lg font-medium text-gray-700 w-36">General Medical History</div>
                          <div className="text-lg text-gray-600 whitespace-pre-wrap flex-1">
                            {selected.medicalHistory || '—'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>                {/* EMR Overview (read-only) */}
                <div className="lg:col-span-2">
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <ClipboardList size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">EMR Overview</span>
                  </h4>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    {emrLoading ? (
                      <div className="text-sm text-gray-600">Loading EMR summary...</div>
                    ) : emrError ? (
                      <div className="text-sm text-red-600">{emrError}</div>
                    ) : emrSummary ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-lg border-2 border-[#2D869C]/30 bg-white p-4 text-center">
                          <div className="text-md text-gray-600 font-semibold ">Appointments</div>
                          <div className="text-2xl font-bold text-[#2D869C] mt-1">{emrSummary.totalAppointments ?? emrSummary.appointmentsCount ?? 0}</div>
                        </div>
                        <div className="rounded-lg border-2 border-[#2D869C]/30 bg-white p-4 text-center">
                          <div className="text-md text-gray-600 font-semibold ">Prescriptions</div>
                          <div className="text-2xl font-bold text-[#2D869C] mt-1">{emrSummary.totalPrescriptions ?? emrSummary.prescriptionsCount ?? 0}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm font-semibold text-gray-600">No EMR summary available</div>
                    )}
                  </div>
                </div>

                {/* All Appointments */}
                <div className="lg:col-span-2">
                  <h4 className="text-md font-semibold flex items-center  text-gray-600 mb-2 uppercase tracking-wide">
                    <Calendar size={20} className="mr-2 text-[#2D869C]" />
                    <span className="text-lg">All Appointments ({(emrSummary?.allAppointments || appointments || []).length})</span>
                  </h4>
                  {appointmentsLoading && !emrSummary ? (
                    <div className="bg-white rounded-lg border-2 border-gray-200 p-6 text-center text-lg font-semibold text-gray-600">Loading appointments...</div>
                  ) : (emrSummary?.allAppointments || appointments || []).length > 0 ? (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {(emrSummary?.allAppointments || appointments).map((apt, idx) => (
                        <div key={apt._id || idx} className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:border-[#2D869C]/50 transition-colors">
                          {/* Card Header */}
                          <div className="bg-gray-50 border-b-2 border-gray-200 px-5 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#2D869C] flex items-center justify-center">
                                <User size={20} className="text-white" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-700 text-lg">
                                  Dr. {apt.doctor?.firstName || apt.doctor?.name || apt.doctorName || '—'} {apt.doctor?.lastName || ''}
                                </div>
                                {apt.doctor?.specialization && (
                                  <div className="text-md text-[#2D869C] font-medium">{apt.doctor.specialization}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {apt.status && (
                                <span className={`px-3 py-1 rounded-full text-md font-semibold ${apt.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                  apt.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                                    apt.status === 'InProgress' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-700'
                                  }`}>{apt.status}</span>
                              )}
                              {apt.appointmentType && (
                                <span className="px-3 py-1 rounded-full text-md font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">{apt.appointmentType}</span>
                              )}
                            </div>
                          </div>

                          {/* Card Body */}
                          <div className="p-5">
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="text-md text-gray-700 font-semibold mb-1">Date & Time</div>
                                <div className="font-semibold text-gray-500 text-md">
                                  {(() => { const d = apt.dateTime || apt.appointmentDate; return d ? new Date(d).toLocaleString() : '—'; })()}
                                </div>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="text-md text-gray-700 font-semibold mb-1">Duration</div>
                                <div className="font-semibold text-gray-500 text-md">{apt.durationMin || '—'} minutes</div>
                              </div>
                            </div>

                            {apt.complaint && (
                              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-l-[#2D869C] border border-gray-200">
                                <div className="text-md text-gray-700 font-semibold mb-1">Chief Complaint</div>
                                <div className="text-md text-gray-500 font-semibold">{apt.complaint}</div>
                              </div>
                            )}

                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => openAppointmentDetail(apt)}
                                className="px-5 py-2.5 text-md bg-[#2D869C] hover:bg-[#236b7a] text-white rounded-lg transition-colors font-semibold flex items-center gap-2"
                              >
                                <Eye size={20} />
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg border-2 border-gray-200 p-4 text-sm font-semibold text-gray-600">No appointments found</div>
                  )}
                </div>

              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border-2 border-gray-300 shadow-sm p-12 text-center">
              <User size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium text-lg">Select a patient to view details</p>
            </div>
          )}
        </div>
      </div>
      {/* Appointment detail modal */}
      {showAppointmentDetail && selectedAppointment && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={closeAppointmentDetail}></div>
            <div className="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-[#2D869C] px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <Calendar size={24} className="mr-3" />
                  Appointment Details
                </h3>
                <button onClick={closeAppointmentDetail} className="text-white hover:text-gray-200 transition-colors p-1 hover:bg-white/20 rounded-lg">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto bg-gray-50">
                {/* Doctor & Time Info Card */}
                <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5">
                  <div className="flex items-start  gap-[3vw]">
                    <div>
                      <div className="text-lg text-gray-500  font-medium mb-[10px]">Doctor</div>
                      <div className="font-bold text-gray-700">Dr. {selectedAppointment.doctor?.firstName || selectedAppointment.doctor?.name || '—'} {selectedAppointment.doctor?.lastName || ''}</div>
                    </div>
                    <div className="flex gap-10">
                      <div>
                        <div className="text-lg text-gray-500 font-medium mb-2">Date & Time</div>
                        <div className="font-bold text-gray-700">{(() => { const d = selectedAppointment.dateTime || selectedAppointment.appointmentDate; return d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'; })()}</div>
                      </div>
                      <div>
                        <div className="text-lg text-gray-500 font-medium mb-1 ml-2">Status</div>
                        <span className={`inline-block px-3 py-1 rounded-full text-md font-semibold ${selectedAppointment.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          selectedAppointment.status === 'InProgress' ? 'bg-yellow-100 text-yellow-800' :
                            selectedAppointment.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-[#9DC9D4]/30 text-[#2D869C]'
                          }`}>{selectedAppointment.status}</span>
                      </div>
                      <div>
                        <div className="text-lg text-gray-500 font-medium mb-2">Duration</div>
                        <div className="font-bold text-gray-700">{selectedAppointment.durationMin} minutes</div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedAppointment.complaint && (
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5 border-l-4 border-l-[#2D869C]">
                    <div className="text-lg text-gray-500 font-medium mb-2">Chief Complaint</div>
                    <div className="text-gray-700 font-bold">{selectedAppointment.complaint}</div>
                  </div>
                )}

                {selectedAppointment.historyOfPresentIllness && (
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5">
                    <div className="text-xs text-gray-500 font-medium mb-2">History of Present Illness</div>
                    <div className="text-gray-700 font-bold whitespace-pre-wrap leading-6">{selectedAppointment.historyOfPresentIllness}</div>
                  </div>
                )}

                {selectedAppointment.physicalExaminationFindings && (
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5">
                    <div className="text-lg text-gray-500 font-medium mb-2">Physical Examination Findings</div>
                    <div className="text-gray-700 font-bold whitespace-pre-wrap leading-6">{selectedAppointment.physicalExaminationFindings}</div>
                  </div>
                )}


                {selectedAppointment.vitals && (
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5">
                    <h4 className="text-lg text-gray-500  font-semibold mb-4">Vital Signs</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedAppointment.vitals.bpSys && selectedAppointment.vitals.bpDia && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Blood Pressure</div>
                          <div className="text-2xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.bpSys}/{selectedAppointment.vitals.bpDia}</div>
                          <div className=" font-semibold text-gray-500">mmHg</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.pulse && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Pulse</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.pulse}</div>
                          <div className="font-semibold text-gray-500">bpm</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.temperature && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold ">Temperature</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.temperature}°F</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.oxygenSaturation && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">SpO2</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.oxygenSaturation}%</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.respiration && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Respiration</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.respiration}</div>
                          <div className="font-semibold text-gray-500">breaths/min</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.sugar && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Blood Sugar</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.sugar}</div>
                          <div className="font-semibold text-gray-500">mg/dL</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.heightCm && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Height</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.heightCm}</div>
                          <div className="font-semibold text-gray-500">cm</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.weightKg && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">Weight</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.weightKg}</div>
                          <div className="font-semibold text-gray-500">kg</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.bmi && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">BMI</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{Number(selectedAppointment.vitals.bmi).toFixed(2)}</div>
                        </div>
                      )}
                      {selectedAppointment.vitals.pft && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-md text-gray-700 uppercase font-semibold">PFT</div>
                          <div className="text-xl font-bold text-[#2D869C] mt-1">{selectedAppointment.vitals.pft}%</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedAppointment.prescriptions && selectedAppointment.prescriptions.length > 0 && (
                  <div className="bg-white rounded-lg border-2 border-gray-200 p-5 mb-5">
                    <h4 className="text-lg text-gray-500 uppercase font-semibold mb-4">Prescriptions ({selectedAppointment.prescriptions.length})</h4>
                    <div className="space-y-4">
                      {(() => {
                        const sorted = [...selectedAppointment.prescriptions]
                          .sort((a, b) => {
                            const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
                            const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
                            return db - da; // descending by createdAt
                          });
                        const total = sorted.length;
                        return sorted.map((prescription, index) => {
                          const displayNumber = total - index; // Highest number for latest
                          return (
                            <div key={prescription._id || index} className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                              {/* Prescription Header */}
                              <div className="bg-gray-50 border-b-2 border-gray-200 px-5 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="bg-[#2D869C] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-md">
                                    {displayNumber}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-700 text-lg">Prescription #{displayNumber}</div>
                                    <div className="text-md text-gray-500 font-semibold">
                                      {prescription.createdAt ? new Date(prescription.createdAt).toLocaleString('en-US', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      }) : '—'}
                                    </div>
                                  </div>
                                </div>
                                {prescription.amount > 0 && (
                                  <div className="bg-[#2D869C] text-white px-4 py-1.5 rounded-full text-md font-bold">
                                    ₹{prescription.amount}
                                  </div>
                                )}
                              </div>

                              <div className="p-5 space-y-4">
                                {/* Medications Section */}
                                {prescription.items && prescription.items.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-1 h-6 bg-[#2D869C] rounded"></div>
                                      <h5 className="text-lg font-semibold text-gray-700">Medications ({prescription.items.length})</h5>
                                    </div>
                                    <div className="overflow-x-auto rounded-lg border-2 border-gray-200">
                                      <table className="min-w-full divide-y divide-gray-200 text-md">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md w-12">#</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Medicine</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Dose</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Frequency</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Route</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Instruction</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md w-24">Days</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                          {prescription.items.map((item, idx) => {
                                            const med = item.medicine || {};
                                            const dose = item.dose ?? (med.doseQuantity ? `${med.doseQuantity}${med.mgPerUnit ? `mg` : ''}` : null);
                                            // Combine timing slots into freq display - timing info IS the frequency
                                            const timingLabel = formatTimingSlots(item.timing || med.timing || {});
                                            const freq = item.frequency || timingLabel || (typeof med.dosesPerDay === 'number'
                                              ? `${med.dosesPerDay} per day`
                                              : '—');
                                            const routeRaw = item.route || med.route || '';
                                            const route = routeRaw ? routeRaw.charAt(0).toUpperCase() + routeRaw.slice(1) : '—';
                                            return (
                                              <tr key={item._id || idx}>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{idx + 1}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-700 break-words text-md">{item.name}</td>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{dose ?? '—'}</td>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{freq || '—'}</td>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{route}</td>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{item.instruction || '—'}</td>
                                                <td className="px-4 py-3 text-gray-700 font-semibold text-md">{item.days || '—'}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Prescribed Tests Section */}
                                {prescription.tests && prescription.tests.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-1 h-6 bg-[#2D869C] rounded"></div>
                                      <h5 className="text-lg font-semibold text-gray-700">Prescribed Tests ({prescription.tests.length})</h5>
                                    </div>
                                    <div className="overflow-x-auto rounded-lg border-2 border-gray-200">
                                      <table className="min-w-full divide-y divide-gray-200 text-md">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md w-12">#</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Test</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                          {prescription.tests.map((test, tidx) => (
                                            <tr key={test._id || tidx}>
                                              <td className="px-4 py-3 text-gray-700 font-semibold text-md">{tidx + 1}</td>
                                              <td className="px-4 py-3 font-semibold text-gray-700 break-words text-md">{test.name || 'Test'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Imaging Tests Section */}
                                {prescription.imagingTests && prescription.imagingTests.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-1 h-6 bg-[#9DC9D4] rounded"></div>
                                      <h5 className="text-lg font-semibold text-gray-700">Imaging Tests ({prescription.imagingTests.length})</h5>
                                    </div>
                                    <div className="overflow-x-auto rounded-lg border-2 border-gray-200">
                                      <table className="min-w-full divide-y divide-gray-200 text-md">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md w-12">#</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Test</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Modality</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Selected Views</th>
                                            <th className="px-4 py-3 text-left font-semibold text-gray-500 text-md">Instructions</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 bg-white">
                                          {prescription.imagingTests.map((img, iidx) => (
                                            <tr key={img._id || iidx}>
                                              <td className="px-4 py-3 text-gray-700 font-semibold text-md">{iidx + 1}</td>
                                              <td className="px-4 py-3 font-semibold text-gray-700 break-words text-md">{img.testName || img.name || 'Imaging Test'}</td>
                                              <td className="px-4 py-3 text-gray-700 font-semibold text-md">{img.modality || '—'}</td>
                                              <td className="px-4 py-3 text-gray-700 font-semibold text-md">{Array.isArray(img.selectedViews) && img.selectedViews.length ? img.selectedViews.join(', ') : '—'}</td>
                                              <td className="px-4 py-3 text-gray-700 font-semibold text-md">{img.instructions || '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Food Advice Section */}
                                {prescription.foodAdvice && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-3">
                                      <div className="w-1 h-6 bg-[#2D869C] rounded"></div>
                                      <h5 className="text-lg font-semibold text-gray-700">Food Advice</h5>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-4">
                                      <div className="flex items-start gap-3">
                                        <span className="text-xl">🍽️</span>
                                        <p className="text-md text-gray-700 font-semibold whitespace-pre-wrap leading-loose">{prescription.foodAdvice}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Prescription Actions */}
                                <div className="pt-4 border-t-2 border-gray-200">
                                  <div className="flex items-center justify-between gap-4">
                                    <label
                                      className="flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={duplicateFlags[prescription._id] || false}
                                        onChange={(e) => toggleDuplicate(prescription._id, e)}
                                        className="w-5 h-5 text-[#2D869C] rounded border-gray-300 focus:ring-2 focus:ring-[#9DC9D4]"
                                      />
                                      <span className="text-md font-semibold text-gray-700">Mark as Duplicate</span>
                                    </label>

                                    <div className="flex items-center gap-3">
                                      <button
                                        onClick={(e) => handleDownloadPDF(prescription, e)}
                                        disabled={generatingPDF === prescription._id}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-[#2D869C] hover:bg-[#9DC9D4] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-md"
                                      >
                                        {generatingPDF === prescription._id ? (
                                          <>
                                            <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            Generating...
                                          </>
                                        ) : (
                                          <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Download Prescription
                                          </>
                                        )}
                                      </button>
                                      <button
                                        onClick={(e) => handlePrint(prescription, e)}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-semibold text-md"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                        Print
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-white border-t-2 border-gray-200 px-6 py-4 flex justify-end">
                <button onClick={closeAppointmentDetail} className="px-6 py-2.5 bg-[#2D869C] hover:bg-[#9DC9D4] text-white font-semibold text-md rounded-lg transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webcam Modal */}
      {showWebcam && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={stopWebcam}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full border-2 border-gray-300">
              <div className="bg-[#2D869C] px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <Camera size={24} className="mr-2" />
                  Capture Patient Photo
                </h3>
                <button onClick={stopWebcam} className="text-white hover:text-gray-200 transition-colors">
                  <X size={24} />
                </button>
              </div>
              <div className="px-6 py-4">
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  {photoUploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-white text-center">
                        <RefreshCw size={48} className="mx-auto mb-3 animate-spin" />
                        <div className="text-lg font-semibold">Uploading photo...</div>
                      </div>
                    </div>
                  )}
                </div>
                <canvas ref={canvasRef} className="hidden" />
                <div className="mt-4 text-md text-gray-700 font-semibold text-center">
                  Position the patient's face in the center of the frame
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
                <button
                  onClick={stopWebcam}
                  className={`${BUTTON_STYLES.secondary} px-5 py-2.5 text-md font-semibold`}
                  disabled={photoUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={capturePhoto}
                  className={`${BUTTON_STYLES.primary} px-6 py-2.5 text-md font-semibold`}
                  disabled={photoUploading}
                >
                  {photoUploading ? (
                    <>
                      <RefreshCw size={18} className="inline mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Camera size={18} className="inline mr-2" />
                      Capture Photo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;
